import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner';
import { Button, Container, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import PropertySelectComponent from './sales/PropertySelectComponent';
import RoomSelectComponent from './sales/RoomSelectComponent';
import BasedOnComponent from './sales/BasedOnComponent';
import DateRangePickerComponent from './sales/DateRangePickerComponentV2';
import { FilterSchema, FilterSchemaType } from '../../../services/sales-report/schema/BookingSchema';
import useBooking from '../../../services/sales-report/hooks/useBooking';
import SalesTableReport from './sales/SalesTableReport';
import SkeletonTable from './sales/SkeletonTable';

const SalesReportComponent: React.FC = () => {
    const queryClient = useQueryClient();

    const { control, handleSubmit, formState: { errors }, setValue, watch } = useForm<FilterSchemaType>({
        resolver: zodResolver(FilterSchema),
        defaultValues: {
            filter: {
                property_id: "",
                base_on: "created_reservation",
                start_date: "",
                end_date: "",
                room_type_id: "",
            },
        },
    });

    // State untuk menyimpan filter yang akan digunakan saat fetching
    const [filter, setFilter] = useState<FilterSchemaType["filter"]>({
        property_id: "",
        base_on: "checkout",
        start_date: "",
        end_date: "",
        room_type_id: "",
    });

    const { data: salesBooking, isFetching: isFetchingSalesBooking } = useBooking({
        filter: filter,
    });

    // Track applied params for refresh logic
    const [appliedParams, setAppliedParams] = useState<{
        property_id: string;
        room_type_id: string;
        base_on: string;
        start_date: string;
        end_date: string;
    } | null>(null);

    const currentFormValues = watch();

    const isFormChanged = appliedParams
        ? currentFormValues.filter?.property_id !== appliedParams.property_id ||
          currentFormValues.filter?.room_type_id !== appliedParams.room_type_id ||
          currentFormValues.filter?.base_on !== appliedParams.base_on ||
          currentFormValues.filter?.start_date !== appliedParams.start_date ||
          currentFormValues.filter?.end_date !== appliedParams.end_date
        : false;

    const showRefresh = !isFetchingSalesBooking && salesBooking && appliedParams !== null && !isFormChanged;

    // Fungsi tunggal untuk menangani submit berdasarkan tombol yang ditekan
    const onSubmit = useCallback(
        (data: FilterSchemaType) => {
            console.log('data', data);

            const updatedFilter = { ...data.filter };

            if (showRefresh) {
                queryClient.invalidateQueries({
                    queryKey: ['report', 'sales']
                });
            }

            setAppliedParams({
                property_id: data.filter.property_id,
                room_type_id: data.filter.room_type_id || "",
                base_on: data.filter.base_on,
                start_date: data.filter.start_date,
                end_date: data.filter.end_date,
            });

            setFilter(updatedFilter);
        },
        [setFilter, showRefresh, queryClient] // Dependensi untuk mencegah pembuatan ulang yang tidak perlu
    );

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetchingSalesBooking && salesBooking) {
            toast.success('Sales report data loaded successfully.');
        }
    }, [isFetchingSalesBooking, salesBooking]);

    return (
        <Container fluid className="dashboard-content">
            <form onSubmit={handleSubmit((data) => onSubmit(data))}>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                    {/* Kiri: Filter Components */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="w-100">
                            <PropertySelectComponent setValue={setValue} />
                        </div>

                        <div className="w-100">
                            <RoomSelectComponent control={control} errors={errors} setValue={setValue} />
                        </div>

                        <div className="w-100">
                            <BasedOnComponent control={control} errors={errors} setValue={setValue} />
                        </div>
                    </div>

                    {/* Kanan: Date Range & Submit */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="d-flex flex-column gap-2 w-100">
                            <DateRangePickerComponent control={control} errors={errors} setValue={setValue} />
                            <Button 
                                variant={showRefresh ? "info" : "warning"} 
                                type="submit" 
                                disabled={isFetchingSalesBooking}
                            >
                                {isFetchingSalesBooking ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefresh ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Sales Report</>
                                ) : (
                                    'Show Sales Report'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
            {isFetchingSalesBooking && <SkeletonTable columns={14} rows={10} />}
            {!isFetchingSalesBooking && salesBooking && (
                <>
                    <SalesTableReport data={salesBooking} />
                </>
            )}
        </Container>
    )
}

export default SalesReportComponent